/**
 * THE TIME CLOCK AND THE TIMESHEET.
 *
 * Anthony, 2026-10-07: "make a clear log in log out and time tracker admin
 * can edit if needed."
 *
 * #/clock is the clock on its own page, for the person who opens the desk
 * to do one thing. It answers one question from across the room: am I on
 * the clock right now? Big words, a colored edge, and a timer that moves,
 * then today's punches written out (In 8:02 AM, Meal 12:00 PM to 12:31 PM,
 * Out 4:45 PM) so nobody has to remember whether they tapped. The same
 * panel sits on My pay, because that is where people already find it.
 *
 * #/timesheet is the week, one row per shift. A worker sees their own and
 * cannot change it, and the page says how to get it fixed. The desk sees
 * everybody, can narrow to one person, and can fix a shift: the times, the
 * meal breaks, a clock-out somebody forgot, a shift nobody punched, or one
 * that should not be there. Every fix needs a reason, and the shift shows
 * who changed it, what it was, and why, to the worker as well.
 *
 * WHERE THIS IS ENFORCED. In the database (migration 0200). The buttons
 * only show for an admin or supervisor, but the functions behind them
 * check the role and the shop themselves, so hiding a button is courtesy
 * and not the lock.
 *
 * The clock card's two-tap clock-out and the meal-break nudge come from the
 * card that lived in pay.ts (0191, 0192), unchanged in what they do.
 */

import {
  clockStatus, clockIn, clockOut, breakStart, breakEnd, meAsTech, technicians,
  weekStart, addDays, type ClockStatus, type Tech,
} from "../data/pay.ts";
import {
  punchesFor, hoursFor, editsFor, punchByHours, editShift, addShift, deleteShift, hourlyIds,
  type SheetPunch,
} from "../data/timeclock.ts";
import {
  shiftMath, punchLines, runningClock, workedSoFar, clockFace, changeLines, editHeadline,
  deletedLine, resolveShift, hhmmOf, dayOf, weekdayIn, FLAG_BADGE, flagTone, type BreakSpan,
} from "../domain/timeclock.ts";
import { buildWeek, dayLabel, type PersonWeek, type SheetRow } from "../domain/timesheet.ts";
import { PACIFIC, ymdIn } from "../domain/zoneclock.ts";
import { esc, mount } from "../ui/dom.ts";
import type { Role } from "../domain/permissions.ts";

const BOSS: Role[] = ["admin", "supervisor"];
const isBoss = (r: Role) => BOSS.includes(r);
/* Who may FIX the clock. Admins only (Anthony, 2026-10-07: "admin and nin
   can", Nina being an admin), and never their own shift, the same rule
   0200 enforces in the database. Supervisors still read every timesheet. */
const canFix = (r: Role) => r === "admin";
let selfTech = "";
const today = () => ymdIn(PACIFIC, new Date());
const fmtH = (n: number) => n.toFixed(2);

/* ── the running timer ─────────────────────────────────────────────── */

/**
 * One interval for the whole app. It stops itself the moment its element
 * is gone, so leaving the page never leaves a timer writing into nothing.
 */
let tick: ReturnType<typeof setInterval> | null = null;

function startTimer(compute: () => number): void {
  if (tick) clearInterval(tick);
  const paint = () => {
    const el = document.getElementById("tclock-timer");
    if (!el || !el.isConnected) { if (tick) clearInterval(tick); tick = null; return; }
    el.textContent = runningClock(compute());
  };
  paint();
  tick = setInterval(paint, 1000);
}

/* ── the clock panel, on #/clock and on My pay ─────────────────────── */

/**
 * What the last tap said ("Logged 7.97 hours."). Kept for a little while
 * rather than written into the panel once, because the screen around the
 * panel redraws after a punch and would wipe a one-time message.
 */
let flash: { text: string; bad: boolean; until: number } | null = null;
const flashHtml = (): string =>
  flash && Date.now() < flash.until ? `<p class="msg ${flash.bad ? "bad" : ""}">${esc(flash.text)}</p>` : "";

/**
 * Draws the clock into `host` and wires it. Returns false when the person
 * cannot clock in (not linked, or not hourly), having drawn nothing, so a
 * caller like My pay can leave the space empty.
 */
export async function drawClockPanel(host: HTMLElement, after?: () => void): Promise<boolean> {
  const [status, me] = await Promise.all([clockStatus(), meAsTech()]);
  if (!status.hourly || !me) { host.innerHTML = ""; return false; }
  const day = today();
  // The fetch is padded a day each side (the filter is UTC), so trim to
  // shifts that started today, Pacific. The open shift can be older, for
  // somebody who forgot to clock out yesterday, so it is looked for in the
  // whole fetch and, failing that, taken from clock_status.
  const fetched = await punchesFor(day, day, me.id).catch(() => [] as SheetPunch[]);
  const mine = fetched.filter((p) => dayOf(p.clock_in) === day || !p.clock_out);
  const open = fetched.find((p) => !p.clock_out)
    ?? (status.open_since ? {
      id: "", technician_id: me.id, clock_in: status.open_since, clock_out: null, hours_id: null,
      breaks: status.on_break_since ? [{ started_at: status.on_break_since, ended_at: null }] : [],
    } : null);
  host.innerHTML = panelHtml(status, mine, open, day);
  wirePanel(host, after);

  const onBreak = open?.breaks.find((b) => !b.ended_at) ?? null;
  if (onBreak) startTimer(() => (Date.now() - Date.parse(onBreak.started_at)) / 1000);
  else if (open) startTimer(() => workedSoFar(open, Date.now()));
  else if (tick) { clearInterval(tick); tick = null; }
  return true;
}

function panelHtml(c: ClockStatus, mine: SheetPunch[], open: SheetPunch | null, day: string): string {
  const openSince = c.open_since ?? open?.clock_in ?? null;
  const onBreak = c.on_break_since ?? open?.breaks.find((b) => !b.ended_at)?.started_at ?? null;
  const state = onBreak ? "brk" : openSince ? "on" : "off";
  const workedMins = open ? workedSoFar(open, Date.now()) / 60 : 0;
  const nudge = !!openSince && !onBreak && (c.break_minutes ?? 0) < 30 && workedMins >= 270;
  const lines = punchLines(mine, day);
  const doneToday = mine.filter((p) => p.clock_out && dayOf(p.clock_in) === day)
    .reduce((t, p) => t + shiftMath(p.clock_in, p.clock_out as string, p.breaks).hours, 0);

  const big = onBreak ? "On a meal break" : openSince ? "You are on the clock" : "Not clocked in";
  const sub = onBreak
    ? `Break started ${esc(clockFace(onBreak))}. 30 minutes or more is unpaid.`
    : openSince
      ? `Clocked in at ${esc(dayOf(openSince) === day ? clockFace(openSince) : `${weekdayIn(openSince)} ${clockFace(openSince)}`)} Pacific.`
      : "Tap Clock in when you start work. Times are Pacific.";
  const timerLabel = onBreak ? "on this break" : openSince ? "worked this shift" : "";

  return `<div class="card pad tclock ${state}" id="tclock" aria-live="polite">
    <div class="tclock-state">
      <span class="tclock-dot" aria-hidden="true"></span>
      <div>
        <div class="tclock-big">${big}</div>
        <div class="tclock-sub">${sub}</div>
      </div>
    </div>
    ${openSince ? `<div class="tclock-timer" id="tclock-timer">0:00:00</div>
      <div class="tclock-timer-l">${timerLabel}</div>`
      : doneToday > 0 ? `<div class="tclock-timer still">${fmtH(doneToday)} h</div>
      <div class="tclock-timer-l">worked today</div>` : ""}
    ${nudge ? `<p class="msg">Take your meal break before you reach 5 hours worked.</p>` : ""}
    <div class="tclock-acts">
      ${openSince ? `<button class="btn ${onBreak ? "primary" : "ghost"}" id="break-btn" data-on="${onBreak ? "1" : ""}">
        ${onBreak ? "End meal break" : "Start meal break"}</button>` : ""}
      ${onBreak ? "" : `<button class="btn ${openSince ? "danger" : "primary"}" id="clock-btn" data-open="${openSince ? "1" : ""}">
        ${openSince ? "Clock out" : "Clock in"}</button>`}
    </div>
    <div class="tclock-today">
      <div class="tclock-today-h">Today</div>
      ${lines.length ? `<ul class="tclock-lines">${lines.map((l) => `
        <li class="${l.live ? "live" : ""}"><span class="tcl-${l.label.toLowerCase()}">${l.label}</span>${esc(l.text)}</li>`).join("")}
      </ul>` : `<p class="quiet small">No punches yet today.</p>`}
    </div>
    <div class="msg-slot" id="clock-msg">${flashHtml()}</div>
  </div>`;
}

function wirePanel(host: HTMLElement, after?: () => void): void {
  const btn = host.querySelector<HTMLButtonElement>("#clock-btn");
  const brk = host.querySelector<HTMLButtonElement>("#break-btn");
  const msg = (m: string, bad = false) => {
    const el = host.querySelector("#clock-msg");
    if (el) el.innerHTML = `<p class="msg ${bad ? "bad" : ""}">${esc(m)}</p>`;
  };
  const redraw = async () => { await drawClockPanel(host, after); after?.(); };

  btn?.addEventListener("click", async () => {
    const open = btn.dataset["open"] === "1";
    // Two taps to clock out, so a pocket tap does not end a shift.
    if (open && btn.dataset["sure"] !== "1") {
      btn.dataset["sure"] = "1"; btn.textContent = "Tap again to clock out";
      return;
    }
    btn.disabled = true; btn.textContent = open ? "Clocking out…" : "Clocking in…";
    try {
      if (open) {
        const r = await clockOut();
        flash = {
          text: r.cancelled ? "Under a minute, so no shift was logged."
            : `Logged ${Number(r.hours).toFixed(2)} hours.${r.flags?.length ? ` ${r.flags.join(". ")}.` : ""}`,
          bad: !!r.flags?.length, until: Date.now() + 20000,
        };
        await redraw();
      } else {
        flash = null;
        await clockIn();
        await redraw();
      }
    } catch (e) {
      btn.disabled = false; btn.dataset["sure"] = "";
      btn.textContent = open ? "Clock out" : "Clock in";
      msg(e instanceof Error ? e.message : String(e), true);
    }
  });
  brk?.addEventListener("click", async () => {
    const on = brk.dataset["on"] === "1";
    brk.disabled = true; brk.textContent = on ? "Ending…" : "Starting…";
    try {
      if (on) await breakEnd(); else await breakStart();
      await redraw();
    } catch (e) {
      brk.disabled = false; brk.textContent = on ? "End meal break" : "Start meal break";
      msg(e instanceof Error ? e.message : String(e), true);
    }
  });
}

/* ── #/clock ───────────────────────────────────────────────────────── */

export async function clockPage(role: Role): Promise<void> {
  mount(`<div class="page tpage">
    <header class="page-head"><a class="back" href="#/more">&larr; Everything else</a>
      <h1>Time clock</h1>
      <p>Clock in when you start. Clock out when you go. Times are Pacific.</p></header>
    <div id="clockhost"><div class="loading">Checking the clock&hellip;</div></div>
    <div id="clockweek"></div>
  </div>`);
  const host = document.getElementById("clockhost");
  const weekHost = document.getElementById("clockweek");
  if (!host || !weekHost) return;

  const me = await meAsTech();
  const drawWeek = async () => {
    if (!me) return;
    const from = weekStart(); const to = addDays(from, 6);
    const week = (await loadWeeks(from, to, me.id)).get(me.id);
    weekHost.innerHTML = `${personCard(me.name, week, false, true)}
      ${readOnlyNote()}
      <a class="btn ghost" href="#/timesheet">See other weeks</a>`;
  };

  try {
    const drew = await drawClockPanel(host, () => void drawWeek());
    if (!drew) {
      host.innerHTML = `<div class="card pad"><p class="quiet">${!me
        ? "Your login is not linked to a person on the team, so there is nothing to clock. An admin can link it on the Team screen."
        : "The time clock is for hourly pay. Your pay plan is not hourly, so there is nothing to clock."}</p></div>`;
    }
    if (drew) await drawWeek();
    if (isBoss(role)) {
      weekHost.insertAdjacentHTML("beforeend", `<a class="btn ghost" href="#/timesheet">Timesheets for everyone</a>`);
    }
  } catch (e) {
    host.innerHTML = `<div class="card"><div class="empty bad">${esc(e instanceof Error ? e.message : String(e))}</div></div>`;
  }
}

/* ── #/timesheet ───────────────────────────────────────────────────── */

let tsFrom = weekStart();
let tsWho = "";

async function loadWeeks(from: string, to: string, techId?: string): Promise<Map<string, PersonWeek>> {
  const [punches, hours] = await Promise.all([punchesFor(from, to, techId), hoursFor(from, to, techId)]);
  const edits = await editsFor(punches.map((p) => p.id), from, to, techId);
  return buildWeek(punches, hours, edits, from, to);
}

export async function timesheetPage(role: Role): Promise<void> {
  const boss = isBoss(role);
  const to = addDays(tsFrom, 6);
  const dt = (iso: string) => new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-US", { timeZone: "UTC", month: "short", day: "numeric" });

  mount(`<div class="page tpage">
    <header class="page-head"><a class="back" href="${boss ? "#/hours" : "#/clock"}">&larr; ${boss ? "Hours" : "Time clock"}</a>
      <h1>${boss ? "Timesheets" : "Your timesheet"}</h1>
      <p>${boss
        ? canFix(role)
          ? "Every shift by the week. Tap Fix on a shift to change its times. Every fix keeps the old times and your reason. Another admin fixes your own shifts."
          : "Every shift by the week. Only an admin can change a shift."
        : "Your shifts by the week. This is what payroll reads."}</p></header>
    <div class="listbar">
      <div class="period">
        <button class="btn ghost sm" id="ts-prev">&lsaquo; Previous</button>
        <span class="period-now">${esc(dt(tsFrom))} &ndash; ${esc(dt(to))}</span>
        <button class="btn ghost sm" id="ts-next">Next &rsaquo;</button>
      </div>
      <button class="btn ghost sm" id="ts-this">This week</button>
    </div>
    <div id="tsbody"><div class="loading">Adding up the week&hellip;</div></div>
  </div>`);

  const move = (days: number) => { tsFrom = addDays(tsFrom, days); void timesheetPage(role); };
  document.getElementById("ts-prev")?.addEventListener("click", () => move(-7));
  document.getElementById("ts-next")?.addEventListener("click", () => move(7));
  document.getElementById("ts-this")?.addEventListener("click", () => { tsFrom = weekStart(); void timesheetPage(role); });

  const body = document.getElementById("tsbody");
  if (!body) return;
  try {
    if (!boss) {
      const me = await meAsTech();
      if (!me) {
        body.innerHTML = `<div class="card pad"><p class="quiet">Your login is not linked to a person on the team, so there are no shifts to show.</p></div>`;
        return;
      }
      const week = (await loadWeeks(tsFrom, to, me.id)).get(me.id);
      body.innerHTML = personCard(me.name, week, false, false) + readOnlyNote();
      return;
    }

    const [techs, hourly, weeks, meTech] = await Promise.all([technicians(), hourlyIds(), loadWeeks(tsFrom, to), meAsTech()]);
    const fixer = canFix(role);
    selfTech = meTech?.id ?? "";
    const nameOf = new Map(techs.map((t) => [t.id, t.name]));
    // Everybody hourly, plus anybody with hours this week, so a typed-in
    // shift for a commission person is not invisible here.
    const people = techs.filter((t) => (t.active && hourly.has(t.id)) || weeks.has(t.id));
    const shown = tsWho ? people.filter((t) => t.id === tsWho) : people;

    body.innerHTML = `
      <div class="tsbar">
        <label class="field"><span>Person</span><select id="ts-who">
          <option value="">Everyone</option>
          ${people.map((t) => `<option value="${esc(t.id)}" ${t.id === tsWho ? "selected" : ""}>${esc(t.name)}</option>`).join("")}
        </select></label>
        ${fixer ? `<button class="btn primary" id="ts-add">Add a missed shift</button>` : ""}
      </div>
      ${shown.length ? shown.map((t) => personCard(t.name, weeks.get(t.id), fixer, false)).join("")
        : `<div class="card pad"><p class="quiet">Nobody is on hourly pay yet. Set a pay plan to hourly on Pay plans and they show here.</p></div>`}`;

    document.getElementById("ts-who")?.addEventListener("change", (e) => {
      tsWho = (e.target as HTMLSelectElement).value; void timesheetPage(role);
    });
    const redraw = () => void timesheetPage(role);
    document.getElementById("ts-add")?.addEventListener("click", () =>
      fixSheet({ mode: "add", techs: people.filter((t) => hourly.has(t.id) && t.id !== selfTech), who: tsWho, day: defaultDay(), done: redraw }));
    const allRows = [...weeks.values()].flatMap((w) => w.rows);
    body.querySelectorAll<HTMLButtonElement>("[data-fix]").forEach((b) => b.addEventListener("click", () => {
      const row = allRows.find((r) => r.key === b.dataset["fix"]);
      if (!row || !row.clockId) return;
      fixSheet({ mode: row.open ? "clockout" : "edit", row, name: nameOf.get(row.technician_id) ?? "Unknown", done: redraw });
    }));
  } catch (e) {
    body.innerHTML = `<div class="card"><div class="empty bad">${esc(e instanceof Error ? e.message : String(e))}</div></div>`;
  }
}

/** Today if it is in the week shown, otherwise the Monday. */
function defaultDay(): string {
  const t = today();
  return t >= tsFrom && t <= addDays(tsFrom, 6) ? t : tsFrom;
}

function readOnlyNote(): string {
  return `<p class="quiet tsnote">If a time is wrong, tell the desk. Only the desk can change the clock,
    and every change shows here with who made it and why.</p>`;
}

/* ── one person's week ─────────────────────────────────────────────── */

function personCard(name: string, w: PersonWeek | undefined, boss: boolean, compact: boolean): string {
  const rows = w?.rows ?? [];
  const s = w?.split ?? { reg: 0, ot: 0, dt: 0 };
  return `<div class="card tsweek">
    <div class="tshead">
      <div class="tshead-who"><b>${esc(name)}</b>${w?.openNow ? `<span class="tsbadge on">On the clock</span>` : ""}</div>
      <div class="tshead-total"><b>${fmtH(w?.total ?? 0)}</b> hours this week</div>
      <div class="tssplit">
        <span>Regular <b>${fmtH(s.reg)}</b></span>
        <span class="${s.ot > 0 ? "ot" : ""}">Overtime <b>${fmtH(s.ot)}</b></span>
        ${s.dt > 0 ? `<span class="ot">Double <b>${fmtH(s.dt)}</b></span>` : ""}
      </div>
    </div>
    ${rows.length ? `<div class="tsgrid">
      <div class="tsrow tscols" aria-hidden="true"><span>Day</span><span>In</span><span>Out</span><span>Meal breaks</span><span>Hours</span></div>
      ${rows.map((r) => shiftRow(r, boss)).join("")}
    </div>` : `<div class="empty">${compact ? "No shifts yet this week." : "No shifts this week."}</div>`}
  </div>`;
}

function mealsCell(bs: BreakSpan[]): string {
  if (!bs.length) return `<span class="dim">None</span>`;
  return bs.map((b) => `<span class="tsmeal">${esc(clockFace(b.started_at))} to ${
    b.ended_at ? esc(clockFace(b.ended_at)) : "now"}</span>`).join("");
}

function shiftRow(r: SheetRow, boss: boolean): string {
  const badges: string[] = [];
  if (r.open) badges.push(`<span class="tsbadge on">On the clock</span>`);
  if (r.kind === "typed") badges.push(`<span class="tsbadge typed">Typed in</span>`);
  if (r.kind === "deleted") badges.push(`<span class="tsbadge bad">Deleted</span>`);
  if (r.offPayroll) badges.push(`<span class="tsbadge bad">Not on payroll</span>`);
  for (const f of r.flags) badges.push(`<span class="tsbadge ${flagTone(f)}" title="${esc(f.text)}">${esc(FLAG_BADGE[f.code])}</span>`);
  if (r.edits.length && r.kind !== "deleted") badges.push(`<span class="tsbadge edited">Edited</span>`);
  const ot = r.split && (r.split.ot > 0 || r.split.dt > 0)
    ? `<small>${r.split.ot > 0 ? `${fmtH(r.split.ot)} OT` : ""}${r.split.dt > 0 ? ` ${fmtH(r.split.dt)} double` : ""}</small>` : "";

  const cells = r.kind === "typed"
    ? `<span class="tscell tswide"><i>In and out</i><span class="dim">${esc(r.note ?? "Typed in on the Hours tab")}</span></span>`
    : `<span class="tscell"><i>In</i><b>${r.clockIn ? esc(clockFace(r.clockIn)) : ""}</b></span>
       <span class="tscell"><i>Out</i><b>${r.clockOut ? esc(clockFace(r.clockOut)) + (r.clockIn && dayOf(r.clockOut) !== dayOf(r.clockIn) ? " <small>next day</small>" : "") : r.kind === "deleted" ? "" : `<span class="dim">Still in</span>`}</b></span>
       <span class="tscell tsmeals"><i>Meal breaks</i>${mealsCell(r.breaks)}</span>`;

  const trail = r.edits.map((e) => {
    const changes = e.action === "delete" ? [deletedLine(e)] : e.action === "add" ? ["It was never punched."] : changeLines(e.before, e.after);
    return `<div class="tsedit"><b>${esc(editHeadline(e))}.</b> ${changes.map((c) => esc(c.endsWith(".") ? c : `${c}.`)).join(" ")}
      <span class="tswhy">Why: ${esc(e.reason)}</span></div>`;
  }).join("");

  const fix = boss && r.clockId && r.kind === "clock" && r.technician_id !== selfTech
    ? `<button class="btn ghost sm tsfix" data-fix="${esc(r.key)}">${r.open ? "Clock out for them" : "Fix this shift"}</button>` : "";

  return `<div class="tsrow ${r.kind} ${r.open ? "open" : ""}">
    <span class="tscell tsday"><b>${esc(dayLabel(r.day))}</b></span>
    ${cells}
    <span class="tscell tshours"><i>Hours</i><b>${r.kind === "deleted" ? `<s>${r.edits.find((e) => e.action === "delete")?.before?.hours != null ? fmtH(Number(r.edits.find((e) => e.action === "delete")?.before?.hours)) : ""}</s>` : r.open ? `<span class="dim">Running</span>` : fmtH(r.hours)}</b>${ot}</span>
    ${badges.length ? `<div class="tsfoot">${badges.join("")}</div>` : ""}
    ${trail ? `<div class="tstrail">${trail}</div>` : ""}
    ${fix ? `<div class="tsfoot tsfixrow">${fix}</div>` : ""}
  </div>`;
}

/* ── the desk's fix sheet ──────────────────────────────────────────── */

type FixOpts =
  | { mode: "edit" | "clockout"; row: SheetRow; name: string; done: () => void }
  | { mode: "add"; techs: Tech[]; who: string; day: string; done: () => void };

/**
 * One sheet for the three fixes. Times are typed as Pacific wall clock on
 * the day the shift started. A time earlier than the clock-in is the next
 * morning, so a late shift is typed the way it reads on a watch. The line
 * under the times works the hours and the flags out as you type, with the
 * same rule the database will use when you save.
 */
export function fixSheet(o: FixOpts): void {
  const row = o.mode === "add" ? null : o.row;
  const day = row?.day ?? (o.mode === "add" ? o.day : today());
  const cin = row?.clockIn ? hhmmOf(row.clockIn) : "";
  const cout = row?.clockOut ? hhmmOf(row.clockOut) : o.mode === "clockout" ? hhmmOf(new Date().toISOString()) : "";
  const breaks = (row?.breaks ?? []).map((b) => ({ start: hhmmOf(b.started_at), end: b.ended_at ? hhmmOf(b.ended_at) : "" }));
  const title = o.mode === "add" ? "Add a missed shift"
    : o.mode === "clockout" ? `Clock out ${o.name}` : `Fix ${o.name}'s shift`;
  const save = o.mode === "add" ? "Add the shift" : o.mode === "clockout" ? "Clock them out" : "Save the fix";
  const who = o.mode === "add" ? "the worker" : o.name.split(/\s+/)[0] ?? "the worker";

  document.getElementById("tssheet")?.remove();
  document.body.insertAdjacentHTML("beforeend", `
    <div class="sheet-back" id="tssheet">
      <div class="sheet" role="dialog" aria-modal="true" aria-labelledby="tss-h">
        <div class="sheet-head"><div><h2 id="tss-h">${esc(title)}</h2></div>
          <button class="sheet-x" id="tss-x" aria-label="Close">&times;</button></div>
        <div class="sheet-body">
          ${o.mode === "add" ? `<label class="field"><span>Who</span><select id="tss-who">
            ${o.techs.map((t) => `<option value="${esc(t.id)}" ${t.id === o.who ? "selected" : ""}>${esc(t.name)}</option>`).join("")}
          </select></label>` : ""}
          <label class="field"><span>Day the shift started</span>
            <input id="tss-day" type="date" value="${esc(day)}" ${o.mode === "clockout" ? "disabled" : ""}></label>
          <div class="tsstimes">
            <label class="field"><span>Clock in</span><input id="tss-in" type="time" value="${esc(cin)}"></label>
            <label class="field"><span>Clock out</span><input id="tss-out" type="time" value="${esc(cout)}"></label>
          </div>
          <p class="quiet small">Pacific time. A clock-out earlier than the clock-in means the next morning.</p>
          <div class="ed-sec">Meal breaks</div>
          <div id="tss-breaks"></div>
          <button class="btn ghost sm" id="tss-addbrk" type="button">Add a meal break</button>
          <p class="tsspreview" id="tss-preview" aria-live="polite"></p>
          <label class="field"><span>Why (required)</span>
            <input id="tss-why" maxlength="300" placeholder="Forgot to clock out, left at 4:30"></label>
          <p class="quiet small">The old times and your reason are kept on the shift. ${esc(who)} will see what changed and why.</p>
          ${o.mode === "edit" ? `<div class="ed-sec">Or delete it</div>
            <p class="quiet small">For a shift punched by mistake or punched twice. It comes off payroll. The reason above is kept.</p>
            <button class="btn danger" id="tss-del" type="button">Delete this shift</button>` : ""}
        </div>
        <div class="sheet-acts">
          <button class="btn primary" id="tss-save">${esc(save)}</button>
          <button class="btn ghost" id="tss-cancel">Cancel</button>
        </div>
        <div class="msg-slot" id="tss-msg"></div>
      </div>
    </div>`);

  const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T | null;
  const shut = () => $("tssheet")?.remove();
  $("tss-x")?.addEventListener("click", shut);
  $("tss-cancel")?.addEventListener("click", shut);
  $("tssheet")?.addEventListener("click", (e) => { if ((e.target as HTMLElement).id === "tssheet") shut(); });
  const fail = (m: string) => { const el = $("tss-msg"); if (el) el.innerHTML = `<p class="msg bad">${esc(m)}</p>`; };

  const list = breaks.slice();
  const drawBreaks = () => {
    const host = $("tss-breaks");
    if (!host) return;
    host.innerHTML = list.length ? list.map((b, i) => `<div class="tssbrk">
        <label class="field"><span>Start</span><input type="time" data-b="${i}" data-k="start" value="${esc(b.start)}"></label>
        <label class="field"><span>End</span><input type="time" data-b="${i}" data-k="end" value="${esc(b.end)}"></label>
        <button class="btn ghost sm" type="button" data-rm="${i}" aria-label="Remove this break">Remove</button>
      </div>`).join("") : `<p class="quiet small">No meal breaks on this shift.</p>`;
    host.querySelectorAll<HTMLInputElement>("input[data-b]").forEach((inp) => inp.addEventListener("input", () => {
      const b = list[Number(inp.dataset["b"])];
      if (b) b[inp.dataset["k"] as "start" | "end"] = inp.value;
      preview();
    }));
    host.querySelectorAll<HTMLButtonElement>("[data-rm]").forEach((x) => x.addEventListener("click", () => {
      list.splice(Number(x.dataset["rm"]), 1); drawBreaks(); preview();
    }));
  };
  const read = () => resolveShift({
    day: ($("tss-day") as HTMLInputElement | null)?.value ?? "",
    clockIn: ($("tss-in") as HTMLInputElement | null)?.value ?? "",
    clockOut: ($("tss-out") as HTMLInputElement | null)?.value ?? "",
    breaks: list,
  });
  const preview = () => {
    const el = $("tss-preview");
    if (!el) return;
    const r = read();
    if ("error" in r) { el.textContent = r.error; el.className = "tsspreview bad"; return; }
    if (!r.clock_out) { el.textContent = "Still on the clock. Hours are worked out at clock-out."; el.className = "tsspreview"; return; }
    const m = shiftMath(r.clock_in, r.clock_out, r.breaks);
    el.className = `tsspreview ${m.flags.length ? "warn" : "ok"}`;
    el.textContent = m.cancelled ? "Under a minute of work. Delete the shift instead."
      : `${fmtH(m.hours)} hours${m.mealMinutes ? `, ${m.mealMinutes} min meal unpaid` : ""}.${
        m.flags.length ? ` ${m.flags.map((f) => f.text).join(". ")}.` : ""}`;
  };
  ["tss-day", "tss-in", "tss-out"].forEach((id) => $(id)?.addEventListener("input", preview));
  $("tss-addbrk")?.addEventListener("click", () => { list.push({ start: "", end: "" }); drawBreaks(); preview(); });
  drawBreaks(); preview();

  $("tss-save")?.addEventListener("click", async () => {
    const b = $("tss-save") as HTMLButtonElement;
    const r = read();
    if ("error" in r) { fail(r.error); return; }
    const why = ($("tss-why") as HTMLInputElement | null)?.value ?? "";
    if (why.trim().length < 3) { fail("Say why. Every change to the clock keeps a reason."); return; }
    if (o.mode !== "edit" || row?.clockOut) {
      if (!r.clock_out) { fail("Pick a clock-out time."); return; }
    }
    b.disabled = true; b.textContent = "Saving…";
    try {
      if (o.mode === "add") {
        const t = ($("tss-who") as HTMLSelectElement | null)?.value ?? "";
        if (!t) throw new Error("Pick who the shift is for.");
        await addShift(t, r, why);
      } else if (row?.clockId) {
        await editShift(row.clockId, r, why);
      }
      shut(); o.done();
    } catch (e) {
      b.disabled = false; b.textContent = save;
      fail(e instanceof Error ? e.message : String(e));
    }
  });

  $("tss-del")?.addEventListener("click", async () => {
    const b = $("tss-del") as HTMLButtonElement;
    const why = ($("tss-why") as HTMLInputElement | null)?.value ?? "";
    if (why.trim().length < 3) { fail("Say why it is going. The reason is kept."); return; }
    // Two taps. Deleting a shift takes somebody's pay for that day.
    if (b.dataset["sure"] !== "1") { b.dataset["sure"] = "1"; b.textContent = "Tap again to delete it"; return; }
    b.disabled = true;
    try {
      if (row?.clockId) await deleteShift(row.clockId, why);
      shut(); o.done();
    } catch (e) {
      b.disabled = false; b.dataset["sure"] = ""; b.textContent = "Delete this shift";
      fail(e instanceof Error ? e.message : String(e));
    }
  });
}

/**
 * The Hours tab hands a clocked row here, so a shift that came from the
 * clock is fixed at the punch and never just at its total.
 */
export async function fixClockedHours(role: Role, hoursId: string, name: string, done: () => void): Promise<boolean> {
  const sb = await punchByHours(hoursId);
  if (!sb) return false;
  if (!canFix(role)) { alert("This shift came from the time clock. Only an admin can change it."); return true; }
  if (sb.technician_id === (await meAsTech())?.id) {
    alert("You cannot change your own shift. Ask another admin to fix it."); return true;
  }
  const row: SheetRow = {
    key: `c:${sb.id}`, technician_id: sb.technician_id, day: dayOf(sb.clock_in), kind: "clock",
    clockId: sb.id, hoursId, clockIn: sb.clock_in, clockOut: sb.clock_out, breaks: sb.breaks,
    hours: 0, open: !sb.clock_out, offPayroll: false, flags: [], split: null, edits: [], note: null,
    sortAt: Date.parse(sb.clock_in),
  };
  fixSheet({ mode: sb.clock_out ? "edit" : "clockout", row, name, done });
  return true;
}
